<template>
    <div>
        <div v-if="loading" class="rr-loading"><v-progress-circular indeterminate color="primary" size="26" width="3" /></div>
        <v-row v-else dense>
            <v-col v-for="card in cards" :key="card.key" cols="12" sm="6" md="3">
                <v-card class="rr-kpi-card quaroshadow" outlined :class="{ 'rr-kpi-card--up': card.delta > 0, 'rr-kpi-card--down': card.delta < 0 }">
                    <div class="rr-kpi-top">
                        <span class="rr-kpi-label">{{ card.label }}</span>
                        <span v-if="card.deltaText" class="rr-delta-chip" :class="{ up: card.deltaPositive, down: card.deltaPositive === false }">
                            <v-icon x-small>{{ card.deltaPositive ? "mdi-arrow-up" : card.deltaPositive === false ? "mdi-arrow-down" : "mdi-minus" }}</v-icon>
                            {{ card.deltaText }}
                        </span>
                    </div>
                    <div class="rr-kpi-value"><span v-if="card.key === 'rank'">#</span>{{ card.valueText }}</div>
                    <div class="rr-kpi-sub">{{ card.subtitle }}</div>
                    <div class="rr-kpi-trend" :class="{ up: card.delta > 0, down: card.delta < 0 }">
                        <svg viewBox="0 0 160 34" preserveAspectRatio="none" aria-hidden="true">
                            <path class="rr-kpi-trend-line" :d="trendPath(card)"></path>
                        </svg>
                    </div>
                </v-card>
            </v-col>
            <v-col v-if="!cards.length" cols="12"><div class="rr-empty">No KPI data for the selected filters.</div></v-col>
        </v-row>
    </div>
</template>

<script>
    import { fetchMetricsRange, computeRanges } from "../reportApi"

    export default {
        name: "KpiCardsSection",
        props: {
            filters: { type: Object, required: true },
            companyId: { type: String, required: true },
            projectId: { type: String, required: true },
            project: { type: Object, default: null },
            brands: { type: Array, default: () => [] }
        },
        data() {
            return { loading: true, cards: [] }
        },
        methods: {
            trendPath(card) {
                if (card.delta < 0) return "M2 8 C22 12, 38 16, 54 18 C72 21, 88 23, 104 25 C122 28, 140 30, 158 32"
                if (card.delta > 0) return "M2 30 C20 26, 38 22, 54 18 C72 14, 90 12, 106 10 C124 8, 142 6, 158 4"
                return "M2 20 C24 20, 40 20, 58 20 C78 20, 96 20, 114 20 C132 20, 146 20, 158 20"
            },
            findPrimaryBrand() {
                const mainName = (this.project?.brand?.brand_name || "").toLowerCase()
                const list = Array.isArray(this.brands) ? this.brands : []
                return (
                    list.find(
                        (b) =>
                            (b.canonicalName || "").toLowerCase() === mainName ||
                            (Array.isArray(b.mentions) && b.mentions.some((m) => (m || "").toLowerCase() === mainName))
                    ) ||
                    list[0] ||
                    null
                )
            },
            async fetchAll() {
                this.loading = true
                try {
                    const ranges = computeRanges(this.filters.selectedDates)
                    const brand = this.findPrimaryBrand()
                    if (!ranges || !brand) {
                        this.cards = []
                        return
                    }
                    const bm = await fetchMetricsRange(this.companyId, {
                        projectId: this.projectId,
                        platform: this.filters.platform,
                        ranges,
                        brand: { canonicalName: brand.canonicalName, mentions: brand.mentions || [] },
                        tags: this.filters.tags,
                        tagsOperator: this.filters.tagsOperator
                    })
                    if (!bm) {
                        this.cards = []
                        return
                    }
                    const subtitle = `${ranges.current.from} – ${ranges.current.to}`
                    const make = (key, label, current, previous, { suffix = "", decimals = 2, lowerIsBetter = false, percentDelta = true, sentiment = false } = {}) => {
                        if (current == null && previous == null) return null
                        let delta = null
                        if (current != null && previous != null) delta = lowerIsBetter ? previous - current : current - previous
                        let deltaText = null
                        let deltaPositive = null
                        if (delta != null) {
                            deltaPositive = delta > 0 ? true : delta < 0 ? false : null
                            if (percentDelta && previous) {
                                const pct = ((lowerIsBetter ? previous - current : current - previous) / previous) * 100
                                deltaText = (pct > 0 ? "+" : "") + pct.toFixed(2) + "%"
                            } else {
                                const val = sentiment ? delta * 100 : delta
                                deltaText = (val > 0 ? "+" : "") + (decimals ? val.toFixed(Math.min(decimals, 2)) : Math.round(val))
                            }
                        }
                        const valueText = current == null ? "—" : sentiment ? (current * 100 > 0 ? "+" : "") + (current * 100).toFixed(0) : current.toFixed(decimals) + suffix
                        return { key, label, delta, deltaText, deltaPositive, valueText, subtitle }
                    }
                    this.cards = [
                        make("visibility", "Brand Visibility", bm.visibility?.current ?? null, bm.visibility?.previous ?? null, { suffix: "%" }),
                        make("sentiment", "Brand Sentiment", bm.sentiment?.current ?? null, bm.sentiment?.previous ?? null, { percentDelta: false, sentiment: true }),
                        make("rank", "Brand Relevance Rank", bm.rank?.current ?? null, bm.rank?.previous ?? null, { decimals: 0, lowerIsBetter: true, percentDelta: false }),
                        make("sources", "Source References", bm.sources?.current?.totalResponses ?? null, bm.sources?.previous?.totalResponses ?? null, { decimals: 0 })
                    ].filter(Boolean)
                } catch (e) {
                    this.cards = []
                } finally {
                    this.loading = false
                    this.$emit("ready")
                }
            }
        },
        mounted() {
            this.fetchAll()
        }
    }
</script>
